import { GMMConfig, RequestOptions, Interceptor } from "./types";
import { retry } from "./utils/retry";
import {
  APIError,
  AuthenticationError,
  RateLimitError,
  GMMError,
} from "./errors";

export class HttpClient {
  private baseUrl: string;
  private interceptors: Interceptor[] = [];

  constructor(private config: GMMConfig) {
    this.baseUrl = "https://api-payment-gateway.zsi.ai/api/v1";
  }

  use(interceptor: Interceptor) {
    this.interceptors.push(interceptor);
  }

  async request<T>(
    method: string,
    path: string,
    body?: any,
    options?: RequestOptions,
  ): Promise<T> {
    const exec = async () => {
      let reqConfig: any = {
        method,
        url: `${this.baseUrl}${path}`,
        headers: {
          "Content-Type": "application/json",

          "gmm-api-key": this.config.apiKey,
          "gmm-api-secret": this.config.apiSecret,
          "gmm-origin": this.config.origin,

          ...(options?.idempotencyKey && {
            "Idempotency-Key": options.idempotencyKey,
          }),
          ...options?.headers,
        },
        body,
      };

      for (const i of this.interceptors) {
        if (i.onRequest) reqConfig = await i.onRequest(reqConfig);
      }

      const controller = new AbortController();
      const timeout = this.config.timeout || 15000;
      const id = setTimeout(() => controller.abort(), timeout);

      try {
        const res = await fetch(reqConfig.url, {
          method: reqConfig.method,
          headers: reqConfig.headers,
          body: reqConfig.body ? JSON.stringify(reqConfig.body) : undefined,
          signal: controller.signal,
        });

        let data = await res.json().catch(() => ({}));

        if (!res.ok) {
          this.handleError(res.status, data);
        }

        for (const i of this.interceptors) {
          if (i.onResponse) data = await i.onResponse(data);
        }

        return data;
      } catch (err: any) {
        for (const i of this.interceptors) {
          if (i.onError) await i.onError(err);
        }

        if (err.name === "AbortError") {
          throw new APIError("Request timeout", 408);
        }

        throw err;
      } finally {
        clearTimeout(id);
      }
    };

    const retries = options?.retries ?? this.config.maxRetries ?? 2;
    return retry(exec, retries);
  }

  private handleError(status: number, data: any): never {
    if (status === 401) throw new AuthenticationError(data.message, status);
    if (status === 429) throw new RateLimitError(data.message, status);
    throw new GMMError(data.message || "API Error", status, data.code);
  }
}
